import React, { useEffect, useRef, useState } from 'react';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { AI_EMAIL_TIMEOUT_MINUTES } from '@/common/constants';

dayjs.extend(utc);

interface AIEmailTimerProps {
  /**
   * UTC timestamp string when AI email was initiated (format: "YYYY-MM-DD HH:mm:ss" or ISO string)
   */
  aiStartTime: string | null | undefined;
  /**
   * Total timeout duration in minutes (default: 60)
   */
  timeoutMinutes?: number;
  /**
   * Callback when timer expires
   */
  onExpire?: () => void;
  /**
   * Format for display: "short" (45 mins 22 secs) or "long" (45 minutes 22 seconds)
   */
  format?: 'short' | 'long';
}

/**
 * AIEmailTimer Component
 *
 * Displays a countdown timer showing remaining time until AI email timeout.
 * Calculates time based on UTC timestamps to ensure timezone independence.
 *
 * @param aiStartTime - UTC timestamp when AI email was initiated
 * @param timeoutMinutes - Total timeout duration (default: 60 minutes)
 * @param onExpire - Callback when timer reaches zero
 * @param format - Display format for time units
 */
const AIEmailTimer: React.FC<AIEmailTimerProps> = ({
  aiStartTime,
  timeoutMinutes = AI_EMAIL_TIMEOUT_MINUTES,
  onExpire,
  format = 'short',
}) => {
  const [remainingTime, setRemainingTime] = useState<{
    minutes: number;
    seconds: number;
    totalSeconds: number;
  } | null>(null);

  // Guard to ensure onExpire is only called once
  const hasExpiredRef = useRef<boolean>(false);
  // Store onExpire in a ref to avoid re-running effect when callback changes
  const onExpireRef = useRef(onExpire);

  // Update the ref whenever onExpire changes
  useEffect(() => {
    onExpireRef.current = onExpire;
  }, [onExpire]);

  useEffect(() => {
    if (!aiStartTime) {
      setRemainingTime(null);
      hasExpiredRef.current = false;
      return;
    }

    const calculateRemainingTime = () => {
      try {
        // Parse start time as UTC
        const startTime = dayjs.utc(aiStartTime);
        const now = dayjs.utc();

        // Calculate elapsed time in seconds
        const elapsedSeconds = now.diff(startTime, 'second');

        // Calculate remaining time
        const totalTimeoutSeconds = timeoutMinutes * 60;
        const remainingSeconds = Math.max(
          0,
          totalTimeoutSeconds - elapsedSeconds
        );

        const minutes = Math.floor(remainingSeconds / 60);
        const seconds = remainingSeconds % 60;

        return {
          minutes,
          seconds,
          totalSeconds: remainingSeconds,
        };
      } catch (error) {
        console.error(
          '[AIEmailTimer] Error calculating remaining time:',
          error
        );
        return null;
      }
    };

    // Calculate immediately
    const initial = calculateRemainingTime();
    setRemainingTime(initial);

    // Check if already expired (only call once)
    if (
      initial &&
      initial.totalSeconds === 0 &&
      onExpireRef.current &&
      !hasExpiredRef.current
    ) {
      hasExpiredRef.current = true;
      console.log(
        '[AIEmailTimer] Timer already expired on mount, calling onExpire once'
      );
      onExpireRef.current();
      return;
    }

    // Update every second
    const interval = setInterval(() => {
      const current = calculateRemainingTime();
      setRemainingTime(current);

      // Check if expired (only call once)
      if (current && current.totalSeconds === 0 && !hasExpiredRef.current) {
        hasExpiredRef.current = true;
        clearInterval(interval);
        console.log('[AIEmailTimer] Timer expired, calling onExpire once');
        if (onExpireRef.current) {
          onExpireRef.current();
        }
      }
    }, 1000);

    return () => {
      clearInterval(interval);
      // Don't reset guard on cleanup - we want to remember we've expired for this timer
      // Guard will reset naturally when component unmounts (new instance = new refs)
    };
  }, [aiStartTime, timeoutMinutes]);

  if (!aiStartTime || remainingTime === null) {
    return <span>---</span>;
  }

  if (remainingTime.totalSeconds === 0) {
    return <span>Expired</span>;
  }

  const minsLabel =
    format === 'short'
      ? 'mins'
      : 'minute' + (remainingTime.minutes !== 1 ? 's' : '');
  const secsLabel =
    format === 'short'
      ? 'secs'
      : 'second' + (remainingTime.seconds !== 1 ? 's' : '');

  return (
    <span>
      {remainingTime.minutes} {minsLabel} {remainingTime.seconds} {secsLabel}
    </span>
  );
};

export default AIEmailTimer;
